/**
 * La Job Description renderizada (spec §8): encabezado con la tabla de
 * información general, resumen, responsabilidades y requisitos, en pestañas
 * ESP / ENG. Debajo de la versión vigente van los botones "Aprobar" y "Pedir
 * cambios" (spec §5): la aprobación es SOLO con el botón — nunca se infiere
 * del texto del chat (lección del Mapa de Talento).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, ChevronRight, Copy, MessageSquareMore } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EnumBadge } from '@/components/ui/enum-badge';
import { cn } from '@/lib/utils';
import { JD_LABELS, jdDocToText } from '@/../../shared/ta-intake';
import type { JDVersionDoc, TaIntakeJdVersion } from '@/../../shared/types/ta-intake';

/** El documento en un idioma: es el mismo bloque para el HM y para el recruiter. */
/**
 * La JD con la estructura del Modelo oficial de TA: el nombre del puesto como
 * título y después Description → Responsibilities → Requirements → Location.
 * El área, a quién reporta y el Impact Level se siguen recolectando pero NO se
 * muestran acá: el Impact Level es interno y este documento sale para afuera.
 */
export function JdDocument({ doc }: { doc: JDVersionDoc }) {
  const L = JD_LABELS;
  return (
    <div className="space-y-4">
      <header>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{L.doc}</p>
        <h3 className="mt-0.5 text-xl font-bold leading-tight">{doc.job_title}</h3>
      </header>
      <section>
        <h4 className="mb-1.5 text-sm font-bold">{L.overview}</h4>
        {doc.overview_paragraphs.map((p, i) => <p key={i} className="mb-2 text-sm leading-relaxed last:mb-0">{p}</p>)}
      </section>
      <section>
        <h4 className="mb-1.5 text-sm font-bold">{L.resp}</h4>
        <ul className="list-disc space-y-1 pl-5 text-sm">{doc.responsibilities.map((r, i) => <li key={i}>{r}</li>)}</ul>
      </section>
      <section>
        <h4 className="mb-1.5 text-sm font-bold">{L.req}</h4>
        <ul className="list-disc space-y-1 pl-5 text-sm">{doc.requirements.map((r, i) => <li key={i}>{r}</li>)}</ul>
      </section>
      <p className="text-sm"><span className="font-bold">{L.loc}</span> {doc.location_and_work_model || '—'}</p>
    </div>
  );
}

export function TaIntakeJdCard({ version, canDecide, pending, onApprove, onRequestChanges, defaultOpen = true }: {
  version: TaIntakeJdVersion;
  /** Es la versión vigente y el HM puede decidir (estado JD_REVIEW). */
  canDecide: boolean;
  /** Hay un turno en vuelo: los botones se bloquean. */
  pending?: boolean;
  onApprove?: (jdVersionId: string) => void;
  onRequestChanges?: (jdVersionId: string) => void;
  defaultOpen?: boolean;
}) {
  const { t } = useTranslation();
  const k = 'talent_acquisition.intake.jd';
  const [open, setOpen] = useState(defaultOpen);
  const [lang, setLang] = useState<'esp' | 'eng'>('esp');
  const [confirm, setConfirm] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(jdDocToText(version[lang], lang));
      toast.success(t(`${k}.copied`));
    } catch {
      toast.error(t(`${k}.copy_failed`));
    }
  };
  const tone = version.status === 'approved' ? 'success' : version.status === 'superseded' ? 'muted' : 'warning';
  return (
    <div className={cn('rounded-xl border bg-card text-card-foreground shadow-xs', version.status === 'approved' && 'border-emerald-300 dark:border-emerald-800')}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 px-4 py-3 text-left">
        {open ? <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />}
        <span className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">{t('talent_acquisition.intake.detail.tab_jd')}</span>
        <span className="truncate font-semibold">{version.esp.job_title}</span>
        <span className="ml-auto flex shrink-0 items-center gap-2">
          <span className="hidden text-xs text-muted-foreground sm:inline">{version.id} · {version.note}</span>
          <EnumBadge value={version.status} tone={tone} label={t(`${k}.version_status.${version.status}`)} />
        </span>
      </button>
      {open && (
        <div className="space-y-4 border-t px-4 py-4">
          <Tabs value={lang} onValueChange={(v) => setLang(v as 'esp' | 'eng')}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <TabsList>
                <TabsTrigger value="esp">ESP</TabsTrigger>
                <TabsTrigger value="eng">ENG</TabsTrigger>
              </TabsList>
              <Button size="sm" variant="ghost" className="gap-1.5" onClick={copiar}><Copy className="h-3.5 w-3.5" />{t(`${k}.copy`)}</Button>
            </div>
            <TabsContent value="esp" className="mt-3"><JdDocument doc={version.esp} /></TabsContent>
            <TabsContent value="eng" className="mt-3"><JdDocument doc={version.eng} /></TabsContent>
          </Tabs>
          {canDecide && (
            <div className="flex flex-wrap items-center gap-2 border-t pt-3">
              <Button size="sm" className="gap-1.5" disabled={pending} onClick={() => setConfirm(true)}>
                <Check className="h-4 w-4" />{t(`${k}.approve`)}
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5" disabled={pending} onClick={() => onRequestChanges?.(version.id)}>
                <MessageSquareMore className="h-4 w-4" />{t(`${k}.request_changes`)}
              </Button>
              <span className="text-xs text-muted-foreground">{t(`${k}.decide_hint`)}</span>
            </div>
          )}
        </div>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t(`${k}.confirm_title`, { version: version.id })}</AlertDialogTitle>
            <AlertDialogDescription>{t(`${k}.confirm_desc`)}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirm(false); onApprove?.(version.id); }}>{t(`${k}.approve`)}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
